import { useRef } from "react";
import type { ChainData } from "../App";
import "./SudokuBoard.css";

type Board = (number | null)[][];

interface SudokuBoardProps {
  board: Board;
  originalBoard: Board;
  uncertain: boolean[][];
  conflicts: boolean[][];
  onChange: (row: number, col: number, value: number | null) => void;
  highlightTarget: string | null;
  highlightRelated: Set<string>;
  candidates?: number[][][] | null;          // 9x9 grid of candidate arrays
  eliminatedCells?: Set<string>;             // cells where candidates were eliminated this step
  causeCells?: Set<string>;                  // cells that caused/triggered this technique
  linkCells?: Set<string>;                   // bridge/conjugate-pair cells (different color)
  highlightedCandidates?: Record<string, number[]>; // cell key -> candidate numbers to highlight
  placedCell?: string | null;                // cell that was placed this step
  chainData?: ChainData | null;              // Renk Zinciri group/link data
}

// Hücre boyutu sabitleri (CSS ile eşleşmeli)
const CELL_SIZE = 48;
const GAP = 1;
const BOX_GAP = 3;

/** Hücre (row,col) → piksel merkez koordinatı */
function cellCenter(row: number, col: number): [number, number] {
  const boxR = Math.floor(row / 3);
  const boxC = Math.floor(col / 3);
  const x = col * (CELL_SIZE + GAP) + boxC * (BOX_GAP - GAP) + CELL_SIZE / 2;
  const y = row * (CELL_SIZE + GAP) + boxR * (BOX_GAP - GAP) + CELL_SIZE / 2;
  return [x, y];
}

export function SudokuBoard({
  board, originalBoard, uncertain, conflicts, onChange,
  highlightTarget, highlightRelated,
  candidates, eliminatedCells, causeCells, linkCells, highlightedCandidates, placedCell, chainData,
}: SudokuBoardProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[][]>(
    Array.from({ length: 9 }, () => Array(9).fill(null))
  );

  // Renk Zinciri grup setleri
  const chainGroupA = new Set<string>();
  const chainGroupB = new Set<string>();
  const chainEliminated = new Set<string>();
  if (chainData) {
    chainData.group_a.forEach(([r, c]) => chainGroupA.add(`${r}-${c}`));
    chainData.group_b.forEach(([r, c]) => chainGroupB.add(`${r}-${c}`));
    chainData.eliminated.forEach(([r, c]) => chainEliminated.add(`${r}-${c}`));
  }

  function handleKeyDown(
    e: React.KeyboardEvent<HTMLInputElement>,
    row: number,
    col: number
  ) {
    const directions: Record<string, [number, number]> = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    };
    if (directions[e.key]) {
      e.preventDefault();
      const [dr, dc] = directions[e.key];
      const newRow = Math.max(0, Math.min(8, row + dr));
      const newCol = Math.max(0, Math.min(8, col + dc));
      inputRefs.current[newRow][newCol]?.focus({ preventScroll: true });
      return;
    }
    // Kilitli hücrelerde hiçbir şey yapma
    if (originalBoard[row][col] !== null) return;

    if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      onChange(row, col, null);
      return;
    }
    // Rakam tuşu: sil gerek kalmadan direkt yaz
    if (/^[1-9]$/.test(e.key)) {
      e.preventDefault();
      onChange(row, col, parseInt(e.key));
    }
  }

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement>,
    row: number,
    col: number
  ) {
    const raw = e.target.value.replace(/[^1-9]/g, "");
    const digit = raw.slice(-1);
    onChange(row, col, digit ? parseInt(digit) : null);
  }

  const ROW_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'J'];

  return (
    <div className="sudoku-board-container">
      {/* Column labels (1-9) */}
      <div className="board-col-labels">
        <div className="label-spacer" />
        {[1,2,3,4,5,6,7,8,9].map(n => (
          <div key={n} className="col-label">{n}</div>
        ))}
      </div>
      <div className="board-with-row-labels">
        {/* Row labels (A-J, skip I) */}
        <div className="board-row-labels">
          {ROW_LETTERS.map(letter => (
            <div key={letter} className="row-label">{letter}</div>
          ))}
        </div>
        <div className="sudoku-board">
        {board.map((rowArr, row) =>
          rowArr.map((cell, col) => {
            const isOriginal = originalBoard[row][col] !== null;
            const isSolved = !isOriginal && cell !== null;
            const isUncertain = uncertain[row]?.[col] === true;
            const isConflict = conflicts[row]?.[col] === true;
            const boxRow = Math.floor(row / 3);
            const boxCol = Math.floor(col / 3);
            const isShaded = (boxRow + boxCol) % 2 === 1;
            const cellKey = `${row}-${col}`;
            const isHighlightTarget = cellKey === highlightTarget;
            const isHighlightRelated = highlightRelated.has(cellKey);
            const isPlacedCell = cellKey === placedCell;
            const isEliminatedCell = eliminatedCells?.has(cellKey) ?? false;
            const isCauseCell = causeCells?.has(cellKey) ?? false;
            const isLinkCell = linkCells?.has(cellKey) ?? false;
            const isChainA = chainGroupA.has(cellKey);
            const isChainB = chainGroupB.has(cellKey);
            const isChainElim = chainEliminated.has(cellKey);

            const cellCandidates = candidates?.[row]?.[col];
            const showCandidates = !cell && cellCandidates && cellCandidates.length > 0;

            return (
              <div
                key={cellKey}
                className={[
                  "sudoku-cell-wrapper",
                  isUncertain ? "uncertain" : "",
                  isConflict ? "conflict" : "",
                ].filter(Boolean).join(" ")}
              >
                {showCandidates ? (
                  <div
                    className={[
                      "sudoku-cell candidate-cell",
                      isShaded ? "shaded" : "",
                      isHighlightTarget ? "highlight-target" : "",
                      isHighlightRelated ? "highlight-related" : "",
                      isEliminatedCell ? "eliminated-cell" : "",
                      isCauseCell ? "cause-cell" : "",
                      isLinkCell ? "link-cell" : "",
                      isChainA ? "chain-group-a" : "",
                      isChainB ? "chain-group-b" : "",
                      isChainElim ? "chain-eliminated" : "",
                    ].filter(Boolean).join(" ")}
                    tabIndex={0}
                    onClick={() => inputRefs.current[row][col]?.focus()}
                  >
                    <div className="candidates-grid">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => {
                        const hlNums = highlightedCandidates?.[cellKey];
                        const isHl = hlNums ? hlNums.includes(n) : false;
                        return (
                          <span
                            key={n}
                            className={[
                              "candidate-num",
                              cellCandidates.includes(n) ? "active" : "",
                              isHl ? "highlighted-candidate" : "",
                            ].filter(Boolean).join(" ")}
                          >
                            {cellCandidates.includes(n) ? n : ""}
                          </span>
                        );
                      })}
                    </div>
                    {isChainA && <span className="chain-label chain-label-a">A</span>}
                    {isChainB && <span className="chain-label chain-label-b">B</span>}
                  </div>
                ) : (
                  <div className={[
                    "sudoku-cell-input-wrapper",
                    isChainA ? "chain-group-a" : "",
                    isChainB ? "chain-group-b" : "",
                    isChainElim ? "chain-eliminated" : "",
                  ].filter(Boolean).join(" ")}>
                    <input
                      ref={(el) => { inputRefs.current[row][col] = el; }}
                      className={[
                        "sudoku-cell",
                        isShaded ? "shaded" : "",
                        isOriginal ? "original" : "",
                        isSolved ? "solved" : "",
                        isUncertain ? "uncertain" : "",
                        isConflict ? "conflict" : "",
                        isHighlightTarget ? "highlight-target" : "",
                        isHighlightRelated ? "highlight-related" : "",
                        isPlacedCell ? "placed-cell" : "",
                        isCauseCell ? "cause-cell" : "",
                        isLinkCell ? "link-cell" : "",
                        isEliminatedCell ? "eliminated-cell" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={cell ?? ""}
                      readOnly={isOriginal || !!candidates}
                      onChange={(e) => handleChange(e, row, col)}
                      onKeyDown={(e) => handleKeyDown(e, row, col)}
                    />
                    {isChainA && <span className="chain-label chain-label-a">A</span>}
                    {isChainB && <span className="chain-label chain-label-b">B</span>}
                  </div>
                )}
                {isUncertain && !isConflict && <span className="uncertain-badge">?</span>}
                {isConflict && <span className="conflict-badge">!</span>}
              </div>
            );
          })
        )}

      {/* Renk Zinciri bağlantı çizgileri (SVG overlay) */}
      {chainData && chainData.links.length > 0 && (
        <svg className="chain-links-overlay" viewBox={`0 0 ${9 * CELL_SIZE + 8 * GAP + 2 * (BOX_GAP - GAP)} ${9 * CELL_SIZE + 8 * GAP + 2 * (BOX_GAP - GAP)}`}>
          <defs>
            <marker id="chain-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
              <path d="M0,0 L6,3 L0,6 Z" fill="rgba(100,116,139,0.5)" />
            </marker>
          </defs>
          {chainData.links.map(([r1, c1, r2, c2], i) => {
            const [x1, y1] = cellCenter(r1, c1);
            const [x2, y2] = cellCenter(r2, c2);
            return (
              <line
                key={i}
                x1={x1} y1={y1} x2={x2} y2={y2}
                className="chain-link-line"
              />
            );
          })}
        </svg>
      )}
      </div>
      </div>
    </div>
  );
}
